import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { type ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { ScreenFailure } from '../channel-selection/screen-failure';

import { AccountForm } from './AccountForm';
import { AccountFound } from './AccountFound';
import { ConsentBlock } from './ConsentBlock';
import { FailureAlert } from './FailureAlert';
import { ReadOnlyHelp } from './ReadOnlyHelp';
import type { ConnectDraft, KeptFields, TradingAccountView } from './trading-account-view';

// A constant, not a text: the i18n lint rule flags string attributes.
const STATUS_ROLE = 'status';

/** Nothing typed yet: every field empty (I7, R2). */
const EMPTY: KeptFields = { accountNumber: '', server: '' };

/** Props of {@link TradingAccountScreen}. */
export interface TradingAccountScreenProps {
  readonly view: TradingAccountView;
  /** What the trader typed in the last submit, without the password (R28), or `null`. */
  readonly kept: KeptFields | null;
  readonly consentBusy: boolean;
  readonly consentFailure: ScreenFailure | null;
  /** Grows with every 422: the consent block remounts, unchecked (R29). */
  readonly consentEpoch: number;
  readonly onSubmit: (draft: ConnectDraft) => void;
  readonly onAuthorize: () => void;
  readonly onRetry: () => void;
  readonly onContinue: () => void;
}

/** A name for what arrived, so focus moves once per result and not on every render (R31). */
function resultKeyOf(view: TradingAccountView): string | null {
  switch (view.kind) {
    case 'found':
      return `found:${String(view.linkKey)}:${view.connectFailure?.reasonCode ?? ''}`;
    case 'connected':
      return 'connected';
    case 'unreachable':
      return `unreachable:${view.connectFailure?.reasonCode ?? ''}`;
    case 'form':
      return view.failure === null ? null : `form:${view.failure.reasonCode}`;
    default:
      return null;
  }
}

/**
 * Screen E4 · Trading account and consent (specs/S6-trading-account/spec.md; US-05; mockup 5,
 * `CompteMT.html`, `CompteLectureSeule.html`). Presentational: no request, no navigation. It owns
 * its `main` landmark because it renders outside `AppLayout` (R1).
 *
 * The form stays mounted through every view that has one (form, verifying, found, unreachable):
 * what the trader typed survives a rejection and the password field is emptied on submit (R4,
 * R28). The consent block is remounted by its `key`, so its box starts unchecked after every new
 * answer of the server (R13, I7). When a result arrives, focus moves to its alert or heading (R31).
 */
export function TradingAccountScreen({
  view,
  kept,
  consentBusy,
  consentFailure,
  consentEpoch,
  onSubmit,
  onAuthorize,
  onRetry,
  onContinue,
}: TradingAccountScreenProps): ReactElement {
  const { t } = useTranslation();
  const resultRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const resultKey = resultKeyOf(view);
  useEffect(() => {
    if (resultKey === null) return;
    // R34 (a): the focus move must not scroll the page under the trader's finger (E18).
    (resultRef.current ?? headingRef.current)?.focus({ preventScroll: true });
  }, [resultKey]);

  const hasForm =
    view.kind === 'form' ||
    view.kind === 'verifying' ||
    view.kind === 'found' ||
    view.kind === 'unreachable';
  // A reconnection starts from the account's server, never its number (R16).
  const initial: KeptFields =
    kept ??
    (view.kind === 'unreachable' ? { accountNumber: '', server: view.summary.server } : EMPTY);
  const submitLabel =
    view.kind === 'unreachable'
      ? 'reconnect'
      : view.kind === 'found' || (view.kind === 'form' && view.rejected)
        ? 'checkAgain'
        : 'check';
  // The refusal of a submit sits above the form, even where a result card also shows (R28).
  const submitFailure =
    view.kind === 'form'
      ? view.failure
      : view.kind === 'found' || view.kind === 'unreachable'
        ? view.connectFailure
        : null;

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.25,
        px: 2.5,
        pt: 2,
        pb: 3.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
        {t('onboarding.tradingAccount.step')}
      </Typography>
      <Typography variant="h1">{t('onboarding.tradingAccount.title')}</Typography>

      {view.kind === 'loading' ? (
        <Box role={STATUS_ROLE} sx={{ color: 'text.secondary' }}>
          {t('onboarding.tradingAccount.loading')}
        </Box>
      ) : null}

      {view.kind === 'readFailed' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FailureAlert failure={view.failure} />
          <Button variant="outlined" onClick={onRetry}>
            {t('onboarding.tradingAccount.retry')}
          </Button>
        </Box>
      ) : null}

      {view.kind === 'connected' ? (
        <>
          <AccountFound
            summary={view.summary}
            title={t('onboarding.tradingAccount.connected.title')}
            headingRef={headingRef}
          />
          <Button variant="contained" size="large" onClick={onContinue} sx={{ minHeight: 52 }}>
            {t('onboarding.tradingAccount.connected.continue')}
          </Button>
        </>
      ) : null}

      {view.kind === 'unreachable' ? (
        <>
          <FailureAlert
            failure={view.failure}
            alertRef={submitFailure === null ? resultRef : undefined}
          />
          <AccountFound summary={view.summary} title={null} />
        </>
      ) : null}
      {submitFailure === null ? null : (
        <FailureAlert
          failure={submitFailure}
          title={
            view.kind === 'form' && view.readOnly
              ? t('onboarding.tradingAccount.readOnly.title')
              : undefined
          }
          alertRef={resultRef}
        />
      )}
      {view.kind === 'form' && view.readOnly ? <ReadOnlyHelp /> : null}

      {view.kind === 'verifying' ? (
        <Box
          role={STATUS_ROLE}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 600 }}
        >
          <CircularProgress size={20} aria-hidden="true" />
          {t('onboarding.tradingAccount.verifying')}
        </Box>
      ) : null}

      {hasForm ? (
        <AccountForm
          initial={initial}
          last4Hint={view.kind === 'unreachable' ? view.summary.last4 : null}
          submitLabel={submitLabel}
          disabled={view.kind === 'verifying'}
          onSubmit={onSubmit}
        />
      ) : null}

      {view.kind === 'found' ? (
        <>
          <AccountFound
            summary={view.summary}
            title={t('onboarding.tradingAccount.found.title')}
            headingRef={submitFailure === null ? headingRef : undefined}
          />
          {view.summary.warnings.map((code) => (
            <FailureAlert key={code} failure={{ reasonCode: code }} tone="warning" />
          ))}
          <ConsentBlock
            key={`${String(view.linkKey)}:${String(consentEpoch)}`}
            last4={view.summary.last4}
            busy={consentBusy}
            failure={consentFailure}
            onAuthorize={onAuthorize}
          />
        </>
      ) : null}
    </Box>
  );
}
